import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import Chart from "react-apexcharts";
import axios from "axios";
import { useSelector } from "react-redux";
import * as SettingSelector from "../../store/setting/selectors";
import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../components/auth/AuthProvider";
import apiUrl from "../../config/api";

const getCssVariable = (variableName, fallback) => {
  if (typeof window === "undefined") {
    return fallback;
  }

  const bodyValue = window.getComputedStyle(document.body).getPropertyValue(variableName).trim();
  const rootValue = window.getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();

  return bodyValue || rootValue || fallback;
};

const toHexColor = (red, green, blue) => (
  `#${[red, green, blue]
    .map((channel) => Math.max(0, Math.min(Number(channel || 0), 255)).toString(16).padStart(2, "0"))
    .join("")}`
);

const normalizeChartColor = (value, fallback) => {
  const color = String(value || "").trim();

  if (!color || color.startsWith("var(")) {
    return fallback;
  }

  if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(color)) {
    return toHexColor(...color.split(","));
  }

  const rgbMatch = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (rgbMatch) {
    return toHexColor(rgbMatch[1], rgbMatch[2], rgbMatch[3]);
  }

  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
    return color;
  }

  return fallback;
};

const getThemeColor = (colorVariable, rgbVariable, fallback) => {
  const directColor = normalizeChartColor(getCssVariable(colorVariable, ""), "");

  if (directColor) {
    return directColor;
  }

  return normalizeChartColor(getCssVariable(rgbVariable, ""), fallback);
};

const getThemePalette = () => {
  const isDark = typeof document !== "undefined" && document.body.classList.contains("dark");

  return {
    mode: isDark ? "dark" : "light",
    text: isDark ? "#dfe4ef" : getThemeColor("--bs-body-color", "--bs-body-color-rgb", "#6c757d"),
    heading: isDark ? "#ffffff" : getThemeColor("--bs-heading-color", "--bs-heading-color-rgb", "#232d42"),
    border: getThemeColor("--bs-border-color", "--bs-border-color-rgb", isDark ? "#3a4151" : "#d8dee8"),
    correct: getThemeColor("--bs-success", "--bs-success-rgb", "#1aa053"),
    wrong: getThemeColor("--bs-danger", "--bs-danger-rgb", "#c03221"),
    unanswered: getThemeColor("--bs-warning", "--bs-warning-rgb", "#f2c037"),
    unseen: getThemeColor("--bs-secondary", "--bs-secondary-rgb", "#8a92a6"),
    primary: getThemeColor("--bs-primary", "--bs-primary-rgb", "#3a57e8"),
  };
};

const setStoredValue = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.warn("Unable to save statistics preference:", error);
  }
};

const Statistics = () => {
  const themeColor = useSelector(SettingSelector.theme_color);
  const themeScheme = useSelector(SettingSelector.theme_scheme);
  const { t } = useLanguage();
  const { isAuthLoading, userDetails } = useAuth();
  const text = t.statisticsPage;
  const storageKey = `quizify-statistics-archive-id-${userDetails?.email || userDetails?.id || "local"}`;

  const [archives, setArchives] = useState([]);
  const [selectedArchiveId, setSelectedArchiveId] = useState("");
  const subjectStorageKey = `quizify-statistics-subject-id-${userDetails?.email || userDetails?.id || "local"}-${selectedArchiveId || "none"}`;
  const [stats, setStats] = useState(null);
  const [isLoadingArchives, setIsLoadingArchives] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [error, setError] = useState("");
  const [themePalette, setThemePalette] = useState(getThemePalette);
  const [selectedSubjectStats, setSelectedSubjectStats] = useState(null);
  const [subjectQuestionType, setSubjectQuestionType] = useState("wrong");
  const [subjectQuestionRows, setSubjectQuestionRows] = useState([]);
  const [subjectQuestionTotal, setSubjectQuestionTotal] = useState(0);
  const [subjectQuestionHasMore, setSubjectQuestionHasMore] = useState(false);
  const [isLoadingSubjectQuestions, setIsLoadingSubjectQuestions] = useState(false);
  const [subjectQuestionSort, setSubjectQuestionSort] = useState({ field: "wrong", direction: "desc" });
  const [selectedProgressSubjectId, setSelectedProgressSubjectId] = useState("");
  const [distributionMode, setDistributionMode] = useState("answers");
  const [distributionQuestionType, setDistributionQuestionType] = useState("all");
  const [overviewQuestionType, setOverviewQuestionType] = useState("all");

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
  }), [token]);

  useEffect(() => {
    if (isAuthLoading) {
      setIsLoadingArchives(true);
      return;
    }

    if (!token) {
      setIsLoadingArchives(false);
      return;
    }

    axios.post(`${apiUrl}/getArchives`, {}, { headers: authHeaders })
      .then((response) => {
        const nextArchives = response.data || [];
        const savedArchiveId = localStorage.getItem(storageKey);
        const savedArchiveExists = nextArchives.some((archive) => archive._id === savedArchiveId);

        setArchives(nextArchives);
        setSelectedArchiveId(savedArchiveExists ? savedArchiveId : nextArchives[0]?._id || "");
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingArchives(false);
      });
  }, [authHeaders, isAuthLoading, storageKey, token]);

  useEffect(() => {
    if (isAuthLoading || !token) {
      return;
    }

    if (!selectedArchiveId) {
      setStats(null);
      return;
    }

    setStoredValue(storageKey, selectedArchiveId);
    setIsLoadingStats(true);
    setError("");

    axios.get(`${apiUrl}/statistics/archive`, {
      params: { archiveId: selectedArchiveId },
      headers: authHeaders,
    })
      .then((response) => {
        setStats(response.data);
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingStats(false);
      });
  }, [authHeaders, isAuthLoading, selectedArchiveId, storageKey, token]);

  const subjects = useMemo(() => stats?.subjects || [], [stats?.subjects]);
  const trend = stats?.trend || [];
  const chartColors = themePalette;
  const selectedProgressSubject = subjects.find((subject) => subject._id === selectedProgressSubjectId) || subjects[0] || null;
  const selectedProgress = selectedProgressSubject?.reviewProgress || null;

  useEffect(() => {
    if (!subjects.length) {
      setSelectedProgressSubjectId("");
      return;
    }

    const savedSubjectId = localStorage.getItem(subjectStorageKey);
    const savedSubjectExists = subjects.some((subject) => subject._id === savedSubjectId);
    setSelectedProgressSubjectId(savedSubjectExists ? savedSubjectId : subjects[0]._id);
  }, [subjectStorageKey, subjects]);

  useEffect(() => {
    if (selectedProgressSubjectId) {
      setStoredValue(subjectStorageKey, selectedProgressSubjectId);
    }
  }, [selectedProgressSubjectId, subjectStorageKey]);

  useEffect(() => {
    if (!selectedArchiveId) {
      return;
    }

    const key = `${subjectStorageKey}-overview-type`;
    const savedType = localStorage.getItem(key);
    setOverviewQuestionType(["all", "closed", "open"].includes(savedType) ? savedType : "all");
  }, [selectedArchiveId, subjectStorageKey]);

  useEffect(() => {
    setThemePalette(getThemePalette());
  }, [themeColor, themeScheme]);

  useEffect(() => {
    setSubjectQuestionRows([]);
    setSubjectQuestionTotal(0);
    setSubjectQuestionHasMore(false);

    if (!selectedSubjectStats || !selectedArchiveId) {
      return;
    }

    fetchSubjectQuestions(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubjectStats?._id, subjectQuestionType, subjectQuestionSort.field, subjectQuestionSort.direction, selectedArchiveId]);

  const selectedSubjectDistribution = distributionQuestionType === "all"
    ? selectedProgressSubject
    : selectedProgressSubject?.distributionByType?.[distributionQuestionType];
  const subjectReviewProgress = selectedProgress?.[distributionQuestionType] || null;
  const subjectReviewCompleted = subjectReviewProgress?.completedPasses > 0
    ? Number(subjectReviewProgress.currentCompleted || 0)
    : Number(subjectReviewProgress?.initiallyCompleted || 0);
  const subjectReviewRemaining = Math.max(0, Number(subjectReviewProgress?.total || 0) - subjectReviewCompleted);
  const isReviewDistribution = distributionMode === "review";
  const donutSeries = isReviewDistribution
    ? [subjectReviewCompleted, subjectReviewRemaining]
    : [
      selectedSubjectDistribution?.correct || 0,
      selectedSubjectDistribution?.wrong || 0,
      selectedSubjectDistribution?.unanswered || 0,
      selectedSubjectDistribution?.unseen || 0,
    ];
  const donutLabels = isReviewDistribution
    ? [text.reviewCompleted, text.reviewRemaining]
    : [text.correct, text.wrong, text.unanswered, text.unseen];
  const donutColors = isReviewDistribution
    ? [chartColors.primary, chartColors.unseen]
    : [chartColors.correct, chartColors.wrong, chartColors.unanswered, chartColors.unseen];
  const hasDistributionData = donutSeries.some((value) => Number(value || 0) > 0);
  const hasSubjectAccuracyData = subjects.some((subject) => Number(subject.total || 0) > 0);
  const hasTrendData = trend.length > 0;
  const donutOptions = {
    chart: {
      foreColor: themePalette.text,
      toolbar: { show: false },
    },
    colors: donutColors,
    labels: donutLabels,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    tooltip: { theme: "light" },
    plotOptions: {
      pie: {
        donut: {
          size: "62%",
          labels: {
            show: true,
            total: {
              show: true,
              label: text.total,
              color: themePalette.text,
              formatter: () => String(
                isReviewDistribution
                  ? subjectReviewProgress?.total || 0
                  : selectedSubjectDistribution?.total || 0
              ),
            },
            name: { color: themePalette.text },
            value: { color: themePalette.heading },
          },
        },
      },
    },
  };

  const lineOptions = {
    chart: {
      foreColor: themePalette.text,
      toolbar: { show: false },
    },
    colors: [themePalette.primary],
    grid: { borderColor: themePalette.border },
    stroke: { curve: "smooth", width: 3 },
    markers: { size: 4 },
    tooltip: { theme: "light" },
    xaxis: {
      categories: trend.map((day) => day.date),
      labels: { style: { colors: trend.map(() => themePalette.text) } },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (value) => `${value}%`,
        style: { colors: [themePalette.text] },
      },
    },
    dataLabels: { enabled: false },
  };
  const lineSeries = [{ name: text.accuracy, data: trend.map((day) => day.accuracy) }];

  const renderSegment = (subject, key, label, color) => {
    const value = subject[key] || 0;
    const width = subject.total ? (value / subject.total) * 100 : 0;

    if (!value) {
      return null;
    }

    return (
      <span
        className="statistics-subject-segment"
        style={{ width: `${width}%`, backgroundColor: color }}
        title={`${label}: ${value}`}
      />
    );
  };

  const renderReviewProgress = (progress, label) => {
    if (!progress?.total) {
      return null;
    }

    const progressLabel = progress.completedPasses > 0
      ? text.progressiveReview(progress.currentPass)
      : text.initialCompletion;
    const completed = progress.completedPasses > 0
      ? progress.currentCompleted
      : progress.initiallyCompleted;
    const percentage = progress.completedPasses > 0
      ? progress.currentPercentage
      : progress.initialPercentage;

    return (
      <div className="statistics-review-progress">
        <div className="statistics-review-progress-heading">
          <strong>{label}</strong>
          <span>{progressLabel}</span>
        </div>
        <div className="statistics-review-progress-bar" aria-label={`${progressLabel}: ${percentage}%`}>
          <span style={{ width: `${percentage}%` }} />
        </div>
        <div className="statistics-review-progress-meta">
          <span>{text.reviewProgressCount(completed, progress.total)}</span>
          <span>{percentage}%</span>
        </div>
        <small>{text.completedPassesDerived}: {progress.completedPasses}</small>
      </div>
    );
  };

  const getDisplayedReviewProgress = (progress) => {
    if (!progress?.total) {
      return { completed: 0, percentage: 0, label: text.initialCompletion };
    }

    return progress.completedPasses > 0
      ? {
        completed: progress.currentCompleted,
        percentage: progress.currentPercentage,
        label: text.progressiveReview(progress.currentPass),
      }
      : {
        completed: progress.initiallyCompleted,
        percentage: progress.initialPercentage,
        label: text.initialCompletion,
      };
  };

  const fetchSubjectQuestions = (offset = 0, replace = false) => {
    if (!selectedSubjectStats || !selectedArchiveId) {
      return;
    }

    setIsLoadingSubjectQuestions(true);

    axios.get(`${apiUrl}/statistics/subject-questions`, {
      params: {
        archiveId: selectedArchiveId,
        subjectId: selectedSubjectStats._id,
        type: subjectQuestionType,
        sortBy: subjectQuestionSort.field,
        sortDirection: subjectQuestionSort.direction,
        offset,
        limit: 25,
      },
      headers: authHeaders,
    })
      .then((response) => {
        setSubjectQuestionRows((previousRows) => (
          replace ? response.data.items || [] : [...previousRows, ...(response.data.items || [])]
        ));
        setSubjectQuestionTotal(Number(response.data.total || 0));
        setSubjectQuestionHasMore(Boolean(response.data.hasMore));
      })
      .catch((requestError) => {
        setError(requestError.response?.data?.message || requestError.message);
      })
      .finally(() => {
        setIsLoadingSubjectQuestions(false);
      });
  };

  const subjectQuestionTabs = [
    { key: "wrong", label: text.mostWrongQuestions, empty: text.noWrongQuestions },
    { key: "seen", label: text.mostSeenQuestions, empty: text.noSeenQuestions },
    { key: "unanswered", label: text.unansweredQuestions, empty: text.noUnansweredQuestions },
    { key: "unseen", label: text.unseenQuestions, empty: text.noUnseenQuestions },
  ];
  const currentSubjectQuestionTab = subjectQuestionTabs.find((tab) => tab.key === subjectQuestionType) || subjectQuestionTabs[0];
  const sortOptions = [
    { value: "insertion:asc", label: text.sortInsertionAsc },
    { value: "insertion:desc", label: text.sortInsertionDesc },
    { value: "alphabetical:asc", label: text.sortAlphabeticalAsc },
    { value: "alphabetical:desc", label: text.sortAlphabeticalDesc },
    { value: "seen:desc", label: text.sortSeenDesc },
    { value: "seen:asc", label: text.sortSeenAsc },
    { value: "wrong:desc", label: text.sortWrongDesc },
    { value: "wrong:asc", label: text.sortWrongAsc },
    { value: "correct:desc", label: text.sortCorrectDesc },
    { value: "correct:asc", label: text.sortCorrectAsc },
    { value: "skipped:desc", label: text.sortSkippedDesc },
    { value: "skipped:asc", label: text.sortSkippedAsc },
  ];
  const changeQuestionSort = (field) => {
    setSubjectQuestionSort((previousSort) => ({
      field,
      direction: previousSort.field === field && previousSort.direction === "desc" ? "asc" : "desc",
    }));
  };
  const renderSortIndicator = (field) => (
    subjectQuestionSort.field === field
      ? (subjectQuestionSort.direction === "asc" ? " ↑" : " ↓")
      : ""
  );

  const renderQuestionStatsTable = () => (
    <div className="statistics-question-group">
      <div className="statistics-question-group-header">
        <h6>{currentSubjectQuestionTab.label}</h6>
        <div className="statistics-question-table-tools">
          <span>{text.loadedQuestions(subjectQuestionRows.length, subjectQuestionTotal)}</span>
          <Form.Select
            size="sm"
            aria-label={text.sortQuestions}
            value={`${subjectQuestionSort.field}:${subjectQuestionSort.direction}`}
            onChange={(event) => {
              const [field, direction] = event.target.value.split(":");
              setSubjectQuestionSort({ field, direction });
            }}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
        </div>
      </div>
      {isLoadingSubjectQuestions && !subjectQuestionRows.length ? (
        <div className="py-4 text-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : subjectQuestionRows.length ? (
        <div className="table-responsive statistics-question-table-wrap">
          <Table className="statistics-question-table" borderless size="sm">
            <colgroup>
              <col className="statistics-question-text-column" />
              <col className="statistics-question-count-column" />
              <col className="statistics-question-count-column" />
              <col className="statistics-question-count-column" />
              <col className="statistics-question-count-column" />
            </colgroup>
            <thead>
              <tr>
                <th><button type="button" onClick={() => changeQuestionSort("insertion")}>{text.question}{renderSortIndicator("insertion")}</button></th>
                <th><button type="button" onClick={() => changeQuestionSort("seen")}>{text.seenCount}{renderSortIndicator("seen")}</button></th>
                <th><button type="button" onClick={() => changeQuestionSort("wrong")}>{text.wrongCount}{renderSortIndicator("wrong")}</button></th>
                <th><button type="button" onClick={() => changeQuestionSort("correct")}>{text.correctCount}{renderSortIndicator("correct")}</button></th>
                <th><button type="button" onClick={() => changeQuestionSort("skipped")}>{text.skippedCount}{renderSortIndicator("skipped")}</button></th>
              </tr>
            </thead>
            <tbody>
              {subjectQuestionRows.map((question) => (
                <tr key={question._id}>
                  <td className="statistics-question-text">{question.questionText || text.questionWithoutText}</td>
                  <td>{question.seenCount}</td>
                  <td>{question.wrongCount}</td>
                  <td>{question.correctCount}</td>
                  <td>{question.skippedCount}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {subjectQuestionHasMore && (
            <div className="text-center mt-3">
              <Button
                disabled={isLoadingSubjectQuestions}
                variant="soft-primary"
                onClick={() => fetchSubjectQuestions(subjectQuestionRows.length, false)}
              >
                {isLoadingSubjectQuestions ? text.loading : text.loadMore}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <p className="mb-0 text-muted">{currentSubjectQuestionTab.empty}</p>
      )}
    </div>
  );

  return (
    <Row>
      <Col lg="12">
        <div className="card" data-aos="fade-up">
          <div className="card-body">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div>
                <p className="mb-1 text-muted">{t.nav.statistics}</p>
                <h3 className="mb-1">{text.title}</h3>
                <p className="mb-0 text-muted">{text.subtitle}</p>
              </div>
              <div className="statistics-archive-select-group">
                <Form.Label className="mb-1">{text.selectArchive}</Form.Label>
                <Form.Select value={selectedArchiveId} onChange={(event) => setSelectedArchiveId(event.target.value)}>
                  {archives.map((archive) => (
                    <option key={archive._id} value={archive._id}>{archive.name}</option>
                  ))}
                </Form.Select>
              </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {isLoadingArchives ? (
              <div className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : !archives.length ? (
              <Alert variant="info" className="mb-0">{text.noArchives}</Alert>
            ) : isLoadingStats ? (
              <div className="py-5 text-center">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : stats ? (
              <>
                <div className="statistics-panel mb-4">
                  <div className="statistics-panel-header statistics-panel-header-inline">
                    <div>
                      <h5>{text.initialCompletion}</h5>
                      <p>{selectedProgress?.all?.hasStarted ? text.selectedSubjectProgress : text.reviewNotStarted}</p>
                    </div>
                    <div className="statistics-subject-select-group">
                      <Form.Label className="mb-1">{text.selectSubject}</Form.Label>
                      <Form.Select
                        value={selectedProgressSubject?._id || ""}
                        onChange={(event) => setSelectedProgressSubjectId(event.target.value)}
                        disabled={!subjects.length}
                      >
                        {subjects.map((subject) => (
                          <option key={subject._id} value={subject._id}>{subject.name}</option>
                        ))}
                      </Form.Select>
                    </div>
                  </div>
                  <div className="statistics-review-grid">
                    {renderReviewProgress(selectedProgress?.all, text.allQuestions)}
                    {renderReviewProgress(selectedProgress?.closed, text.closedQuestions)}
                    {renderReviewProgress(selectedProgress?.open, text.openQuestions)}
                  </div>
                </div>
                <Row className="g-4">
                  <Col xl="4" lg="6">
                    <div className="statistics-panel h-100">
                      <div className="statistics-panel-header">
                        <h5>{text.subjectDistributionTitle}</h5>
                        <p>{text.subjectDistributionSubtitle}</p>
                      </div>
                      <div className="statistics-chart-controls statistics-chart-controls-three">
                        <Form.Select
                          aria-label={text.selectSubject}
                          value={selectedProgressSubject?._id || ""}
                          onChange={(event) => setSelectedProgressSubjectId(event.target.value)}
                        >
                          {subjects.map((subject) => (
                            <option key={subject._id} value={subject._id}>{subject.name}</option>
                          ))}
                        </Form.Select>
                        <Form.Select value={distributionMode} onChange={(event) => setDistributionMode(event.target.value)}>
                          <option value="answers">{text.subjectAnswerDistribution}</option>
                          <option value="review">
                            {subjectReviewProgress?.completedPasses > 0
                              ? text.reviewDistribution(subjectReviewProgress.currentPass)
                              : text.studyProgressDistribution}
                          </option>
                        </Form.Select>
                        <Form.Select value={distributionQuestionType} onChange={(event) => setDistributionQuestionType(event.target.value)}>
                          <option value="all">{text.allQuestions}</option>
                          <option value="closed">{text.closedQuestions}</option>
                          <option value="open">{text.openQuestions}</option>
                        </Form.Select>
                      </div>
                      {hasDistributionData ? (
                        <>
                          <Chart options={donutOptions} series={donutSeries} type="donut" height="290" />
                          <div className="statistics-legend statistics-donut-legend">
                            {donutLabels.map((label, index) => (
                              <span key={label}><i style={{ backgroundColor: donutColors[index] }} />{label}</span>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="statistics-empty-chart">{text.noDistribution}</div>
                      )}
                    </div>
                  </Col>
                  <Col xl="4" lg="6">
                    <div className="statistics-panel h-100">
                      <div className="statistics-panel-header">
                        <h5>{text.accuracyBySubjectTitle}</h5>
                        <p>{text.accuracyBySubjectSubtitle}</p>
                      </div>
                      {hasSubjectAccuracyData ? (
                        <div className="statistics-accuracy-list">
                          {subjects.map((subject) => (
                            <div className="statistics-accuracy-item" key={subject._id}>
                              <div className="statistics-accuracy-heading">
                                <span>{subject.name}</span>
                                <strong>{subject.accuracy}%</strong>
                              </div>
                              <div className="statistics-accuracy-bar">
                                <span style={{ width: `${subject.accuracy}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="statistics-empty-chart">{text.noSubjectAccuracy}</div>
                      )}
                    </div>
                  </Col>
                  <Col xl="4">
                    <div className="statistics-panel h-100">
                      <div className="statistics-panel-header">
                        <h5>{text.trendTitle}</h5>
                        <p>{text.trendSubtitle}</p>
                      </div>
                      {hasTrendData ? (
                        <Chart options={lineOptions} series={lineSeries} type="line" height="330" />
                      ) : (
                        <div className="statistics-empty-chart">{text.noTrend}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <div className="statistics-panel mt-4">
                  <div className="statistics-panel-header statistics-panel-header-inline">
                    <div>
                      <h5>{text.subjectOverviewTitle}</h5>
                      <p>{text.subjectOverviewSubtitle}</p>
                    </div>
                    <div className="statistics-overview-tools">
                      <div className="statistics-legend">
                        <span><i style={{ backgroundColor: chartColors.correct }} />{text.correct}</span>
                        <span><i style={{ backgroundColor: chartColors.wrong }} />{text.wrong}</span>
                        <span><i style={{ backgroundColor: chartColors.unanswered }} />{text.unanswered}</span>
                        <span><i style={{ backgroundColor: chartColors.unseen }} />{text.unseen}</span>
                      </div>
                      <Form.Group className="statistics-overview-basis">
                        <Form.Label>{text.completionBasedOn}</Form.Label>
                        <Form.Select
                          size="sm"
                          value={overviewQuestionType}
                          onChange={(event) => {
                            setOverviewQuestionType(event.target.value);
                            setStoredValue(`${subjectStorageKey}-overview-type`, event.target.value);
                          }}
                        >
                          <option value="all">{text.allQuestions}</option>
                          <option value="closed">{text.closedQuestions}</option>
                          <option value="open">{text.openQuestions}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>

                  <div className="statistics-subject-list">
                    {subjects.map((subject) => (
                      <div className="statistics-subject-row" key={subject._id}>
                        <div className="statistics-subject-name" title={subject.name}>{subject.name}</div>
                        <div className="statistics-subject-bars">
                          <div className="statistics-subject-bar">
                            {renderSegment(subject, "correct", text.correct, chartColors.correct)}
                            {renderSegment(subject, "wrong", text.wrong, chartColors.wrong)}
                            {renderSegment(subject, "unanswered", text.unanswered, chartColors.unanswered)}
                            {renderSegment(subject, "unseen", text.unseen, chartColors.unseen)}
                          </div>
                          {(() => {
                            const review = getDisplayedReviewProgress(subject.reviewProgress?.[overviewQuestionType]);
                            return (
                              <div className="statistics-subject-review-line">
                                <span>{review.label}</span>
                                <div className="statistics-subject-review-bar">
                                  <span style={{ width: `${review.percentage}%` }} />
                                </div>
                                <strong>{review.percentage}%</strong>
                              </div>
                            );
                          })()}
                        </div>
                        <Button
                          className="statistics-subject-details-btn"
                          variant="soft-primary"
                          size="sm"
                          onClick={() => {
                            setSubjectQuestionType("wrong");
                            setSubjectQuestionSort({ field: "wrong", direction: "desc" });
                            setSelectedSubjectStats(subject);
                          }}
                        >
                          {text.details}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Col>

      <Modal
        centered
        className="statistics-subject-modal"
        scrollable
        size="xl"
        show={Boolean(selectedSubjectStats)}
        onHide={() => setSelectedSubjectStats(null)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedSubjectStats?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSubjectStats && (
            <>
              <Row className="g-3 mb-4">
                <Col md="3" xs="6">
                  <div className="statistics-subject-metric">
                    <span>{text.total}</span>
                    <strong>{selectedSubjectStats.total}</strong>
                  </div>
                </Col>
                <Col md="3" xs="6">
                  <div className="statistics-subject-metric">
                    <span>{text.correct}</span>
                    <strong>{selectedSubjectStats.correct}</strong>
                  </div>
                </Col>
                <Col md="3" xs="6">
                  <div className="statistics-subject-metric">
                    <span>{text.wrong}</span>
                    <strong>{selectedSubjectStats.wrong}</strong>
                  </div>
                </Col>
                <Col md="3" xs="6">
                  <div className="statistics-subject-metric">
                    <span>{text.unseen}</span>
                    <strong>{selectedSubjectStats.unseen}</strong>
                  </div>
                </Col>
              </Row>

              <div className="statistics-review-grid mb-4">
                {renderReviewProgress(selectedSubjectStats.reviewProgress?.all, text.allQuestions)}
                {renderReviewProgress(selectedSubjectStats.reviewProgress?.closed, text.closedQuestions)}
                {renderReviewProgress(selectedSubjectStats.reviewProgress?.open, text.openQuestions)}
              </div>

              <div className="statistics-question-tabs">
                {subjectQuestionTabs.map((tab) => (
                  <Button
                    key={tab.key}
                    variant={subjectQuestionType === tab.key ? "primary" : "soft-primary"}
                    size="sm"
                    onClick={() => {
                      setSubjectQuestionType(tab.key);
                      setSubjectQuestionSort(
                        tab.key === "seen"
                          ? { field: "seen", direction: "desc" }
                          : tab.key === "unanswered"
                            ? { field: "skipped", direction: "desc" }
                            : tab.key === "unseen"
                              ? { field: "insertion", direction: "asc" }
                              : { field: "wrong", direction: "desc" }
                      );
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>

              {renderQuestionStatsTable()}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedSubjectStats(null)}>
            {text.close}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default Statistics;
